import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { createSocket } from "@/lib/socket";
import { useAuth } from "@/context/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import { toast } from "sonner";
import { MagnifyingGlass, Star, Storefront, ShieldCheck, Users, Package } from "@phosphor-icons/react";

export default function StoreProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const socketRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async () => {
    try {
      const { data } = await api.get(`/store/${slug}`, { params: { q: debouncedQ || undefined } });
      setData(data);
    } catch { toast.error("Mağaza yüklenemedi"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, debouncedQ]);

  // SEO title
  useEffect(() => {
    if (data?.seo?.title) document.title = data.seo.title;
    return () => { document.title = "kolaysatis.com"; };
  }, [data?.seo?.title]);

  useEffect(() => {
    if (!user) return;
    api.get("/me/follows").then(({ data }) => {
      setIsFollowing(data.some((u) => u.store?.slug === slug));
    });
  }, [user, slug]);

  useEffect(() => {
    const s = createSocket();
    socketRef.current = s;
    s.on("connect", () => s.emit("join_store", { slug }));
    s.on("store_new_listing", () => { toast("Yeni ilan geldi!"); load(); });
    return () => { if (s.connected) s.disconnect(); };
    // eslint-disable-next-line
  }, [slug]);

  const toggleFollow = async () => {
    if (!user) return nav("/giris");
    setBusy(true);
    try {
      if (isFollowing) {
        await api.delete(`/store/${slug}/follow`);
        setIsFollowing(false); toast("Takipten çıkıldı");
      } else {
        await api.post(`/store/${slug}/follow`);
        setIsFollowing(true); toast.success("Takip edildi");
      }
      load();
    } finally { setBusy(false); }
  };

  if (!data) return <div className="max-w-[1400px] mx-auto px-6 py-16 text-slate-500">Yükleniyor…</div>;

  const store = data.store || {};
  const u = data.user || {};

  return (
    <div data-testid="store-page">
      {/* HERO BANNER */}
      <div className="relative w-full h-56 md:h-72 lg:h-80 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
        {store.banner && (
          <img
            src={store.banner.startsWith("http") ? store.banner : fileUrl(store.banner)}
            alt=""
            className="w-full h-full object-cover opacity-70"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </div>

      {/* HEADER CARD — overlaps banner */}
      <div className="max-w-[1400px] mx-auto px-6 -mt-16 md:-mt-20 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200/70 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Round logo with badge */}
          <div className="relative flex-none -mt-16 md:-mt-20">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full ring-4 ring-white bg-white shadow-2xl overflow-hidden">
              {store.logo ? (
                <img
                  src={store.logo.startsWith("http") ? store.logo : fileUrl(store.logo)}
                  alt={store.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center">
                  <Storefront size={44} weight="fill" />
                </div>
              )}
            </div>
            {u.is_verified && (
              <span className="absolute bottom-1 right-1 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-md ring-2 ring-white">
                <ShieldCheck size={16} weight="fill" />
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-blue-600 mb-1 flex items-center gap-2">
              Kurumsal Mağaza
              {u.is_verified && (
                <span className="text-emerald-700 flex items-center gap-1 normal-case tracking-normal">
                  <ShieldCheck size={12} weight="fill" /> Onaylı
                </span>
              )}
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-slate-900">{store.name}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-slate-600 tracking-wide">
              <span className="flex items-center gap-1">
                <Star size={14} weight="fill" className="text-amber-500" />
                <span className="font-semibold text-slate-800">{u.seller_rating?.toFixed(1) || "—"}</span>
                <span className="text-slate-400">({u.review_count || 0})</span>
              </span>
              <span className="flex items-center gap-1"><Users size={14} weight="fill" className="text-slate-400" /> {store.followers_count || 0} takipçi</span>
              <span className="flex items-center gap-1"><Package size={14} weight="fill" className="text-slate-400" /> {data.listings.length} ilan</span>
            </div>
            {store.bio && <p className="text-sm text-slate-600 mt-3 max-w-2xl leading-relaxed tracking-wide">{store.bio}</p>}

            {/* Trust badges */}
            {(data.badges && data.badges.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-4" data-testid="store-badges">
                {data.badges.map((b) => {
                  const tone = {
                    slate: "bg-slate-100 text-slate-700",
                    emerald: "bg-emerald-50 text-emerald-800",
                    blue: "bg-blue-50 text-blue-800",
                    amber: "bg-amber-50 text-amber-800",
                  }[b.tone] || "bg-slate-100 text-slate-700";
                  return (
                    <span key={b.id} data-testid={`store-badge-${b.id}`}
                      className={`text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full ${tone}`}>
                      {b.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Trust snapshot */}
            {data.trust && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-slate-500" data-testid="store-trust">
                <span>Güven skoru: <span className="font-mono-tight font-medium text-slate-900">{data.trust.trust_score}/100</span></span>
                <span>Başarılı satış: <span className="font-mono-tight text-slate-900">{data.trust.successful_sales}</span></span>
                <span>İptal: <span className="font-mono-tight text-slate-900">{data.trust.cancellations}</span></span>
              </div>
            )}
          </div>

          <div className="flex gap-2 w-full md:w-auto">
            {user && user.user_id !== u.user_id && (
              <button
                data-testid="follow-btn"
                onClick={toggleFollow}
                disabled={busy}
                className={`w-full md:w-auto px-6 py-3 rounded-full font-semibold text-sm transition-all disabled:opacity-60 ${
                  isFollowing
                    ? "bg-white text-slate-800 border border-slate-200 hover:border-red-400 hover:text-red-600 hover:bg-red-50"
                    : "bg-slate-900 text-white hover:bg-slate-800 shadow-lg shadow-slate-900/20 hover:-translate-y-0.5"
                }`}
              >
                {isFollowing ? "Takipteyim ✓" : "Takip Et"}
              </button>
            )}
          </div>
        </div>

        {/* Search bar — Apple/Stripe minimal */}
        <div className="mt-10 flex items-center bg-white rounded-2xl shadow-sm border border-slate-200/70 focus-within:border-blue-600 focus-within:shadow-md focus-within:ring-4 focus-within:ring-blue-100 transition-all max-w-lg">
          <MagnifyingGlass size={18} className="ml-4 text-slate-400" />
          <input
            data-testid="store-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Bu mağazada ara..."
            className="w-full bg-transparent px-3 py-3.5 outline-none text-sm text-slate-800 placeholder:text-slate-400 tracking-wide"
          />
          {q && (
            <button onClick={() => setQ("")} className="mr-3 text-xs text-slate-400 hover:text-slate-700">Temizle</button>
          )}
        </div>

        {/* Best sellers widget */}
        {data.best_sellers && data.best_sellers.length > 0 && (
          <section className="mt-10 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-3xl p-6 shadow-sm" data-testid="best-sellers-widget">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-700 mb-1 flex items-center gap-2">
                  <Star size={12} weight="fill" /> En Çok İlgi Gören
                </p>
                <h3 className="font-display text-xl font-bold text-slate-900 tracking-tight">Mağazanın Yıldızları</h3>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Top 3 · Teklif sayısına göre</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {data.best_sellers.map((a, i) => (
                <Link
                  key={a.id}
                  to={`/mezat/${a.id}`}
                  data-testid={`best-seller-${i}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                    {a.images?.[0] && (
                      <img
                        src={a.images[0].startsWith("http") ? a.images[0] : fileUrl(a.images[0])}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    <span className={`absolute top-2 left-2 font-mono-tight text-xl font-black text-white px-2 py-0.5 rounded-lg ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : "bg-amber-800"}`}>#{i + 1}</span>
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-sm text-slate-900 line-clamp-2 tracking-tight leading-snug">{a.title}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <span className="font-mono-tight text-base font-black text-blue-700">₺{Number(a.current_price).toLocaleString("tr-TR")}</span>
                      <span className="text-xs font-bold text-slate-500">{a.bid_count} teklif</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10 pb-16">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-4">
            Mağaza İlanları <span className="text-slate-400 font-medium">({data.listings.length})</span>
          </h2>
          {data.listings.length === 0 ? (
            <p className="text-slate-500 py-8 tracking-wide">Bu mağazada ilan bulunamadı.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.listings.map((a) => <AuctionCard key={a.id} auction={a} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
